import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../services/config';
import companyService from '../services/companyService';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  Avatar,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search, Visibility, Edit, Delete } from '@mui/icons-material';

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  company?: string;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editStatus, setEditStatus] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [createForm, setCreateForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'field_worker',
    companyId: '',
    phone: '',
  });
  const ROLE_OPTIONS = ['admin','project_manager','supervisor','safety_officer','field_worker','contractor'];

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const baseURL = API_BASE_URL;
        const token = localStorage.getItem('token');
        const res = await axios.get(`${baseURL}/users`, {
          params: { page: 1, limit: 100 },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data?.users)
          ? res.data.users
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const mapped: UserRow[] = list.map((u: any) => ({
          id: String(u._id || u.id),
          name: String(u.name || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || 'Unknown'),
          email: String(u.email || '—'),
          role: String(u.role || 'user'),
          status: String(u.status || (u.isActive ? 'active' : 'inactive') || 'active'),
          company: String((u?.company && (u.company.name || u.company.legalName)) || ''),
        }));

        setUsers(mapped);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const prepareCreate = async () => {
      if (!createOpen) return;
      try {
        const baseURL = API_BASE_URL;
        const token = localStorage.getItem('token');
        const me = await axios.get(`${baseURL}/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const user = me?.data?.user || me?.data;
        const roleRaw = String(user?.role || '').toLowerCase();
        const adminRole = roleRaw.includes('admin');
        setIsAdmin(adminRole);

        const list = await companyService.getAllCompanies();
        const mapped = Array.isArray(list)
          ? list.map((c: any) => ({ id: String(c._id || c.id), name: String(c.name || c.legalName || 'Unknown') }))
          : [];
        setCompanies(mapped);

        // Preselect current company if only one in list
        if (mapped.length === 1) {
          setCreateForm(prev => ({ ...prev, companyId: mapped[0].id }));
        }
      } catch (e) {
        console.warn('Failed to prepare create user dialog:', e);
        setIsAdmin(false);
        setCompanies([]);
      }
    };
    prepareCreate();
  }, [createOpen]);

  const filtered = users.filter(u =>
    `${u.name} ${u.email} ${u.role} ${u.company ?? ''}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const roleColor = (role: string) => {
    if (role === 'admin') return 'error';
    if (['project_manager','supervisor'].includes(role)) return 'warning';
    return 'primary';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">User Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search users..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} /> }}
            sx={{ minWidth: 280 }}
          />
          <Button variant="contained" onClick={() => setCreateOpen(true)}>
            Create User
          </Button>
        </Box>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {u.name.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">{u.name}</Typography>
                        <Typography variant="caption" color="text.secondary">ID: {u.id}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={u.role.replace('_',' ')} color={roleColor(u.role) as any} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{u.company || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={u.status} color={u.status === 'active' ? 'success' : 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => { setSelectedUser(u); setViewOpen(true); }}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setSelectedUser(u); setEditRole(u.role); setEditStatus(u.status); setEditOpen(true); }}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deactivate">
                      <IconButton size="small" color="error" onClick={async () => {
                        try {
                          const baseURL = API_BASE_URL;
                          const token = localStorage.getItem('token');
                          await axios.delete(`${baseURL}/users/${u.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                          setUsers(prev => prev.map(p => p.id === u.id ? { ...p, status: 'inactive' } : p));
                        } catch (err) {
                          console.error('Failed to deactivate user:', err);
                        }
                      }}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">No users found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View User Dialog */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 40, height: 40 }}>
                  {selectedUser.name.split(' ').map(n => n[0]).join('')}
                </Avatar>
                <Box>
                  <Typography variant="body1" fontWeight="medium">{selectedUser.name}</Typography>
                  <Typography variant="caption" color="text.secondary">ID: {selectedUser.id}</Typography>
                </Box>
              </Box>
              <Typography variant="body2">Email: {selectedUser.email}</Typography>
              <Typography variant="body2">Role: {selectedUser.role}</Typography>
              <Typography variant="body2">Status: {selectedUser.status}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Role"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                sx={{ mb: 3 }}
              />
              <TextField
                fullWidth
                label="Status"
                value={editStatus}
                onChange={(e) => setEditStatus(e.target.value)}
                sx={{ mb: 3 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={async () => {
            if (!selectedUser) return;
            try {
              const baseURL = API_BASE_URL;
              const token = localStorage.getItem('token');
              const payload: any = {};
              if (editRole && editRole !== selectedUser.role) payload.role = editRole;
              if (editStatus) payload.isActive = editStatus === 'active';
              if (Object.keys(payload).length > 0) {
                await axios.put(`${baseURL}/users/${selectedUser.id}`, payload, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
                setUsers(prev => prev.map(p => p.id === selectedUser.id ? { ...p, role: editRole, status: editStatus } : p));
              }
              setEditOpen(false);
            } catch (err) {
              console.error('Failed to update user:', err);
            }
          }}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="First Name"
              value={createForm.firstName}
              onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Last Name"
              value={createForm.lastName}
              onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Temporary Password"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Minimum 6 characters"
            />
            <TextField
              fullWidth
              label="Phone"
              value={createForm.phone}
              onChange={(e) => setCreateForm(prev => ({ ...prev, phone: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              select
              SelectProps={{ native: true }}
              label="Role"
              value={createForm.role}
              onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value }))}
              sx={{ mb: 2 }}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r} value={r}>{r.replace('_',' ')}</option>
              ))}
            </TextField>
            {isAdmin && (
              <TextField
                fullWidth
                select
                SelectProps={{ native: true }}
                label="Company (admin only)"
                value={createForm.companyId}
                onChange={(e) => setCreateForm(prev => ({ ...prev, companyId: e.target.value }))}
                sx={{ mb: 2 }}
              >
                <option value="">Select company...</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={createLoading}
            onClick={async () => {
              try {
                setCreateLoading(true);
                const baseURL = API_BASE_URL;
                const token = localStorage.getItem('token');
                const headers = token ? { Authorization: `Bearer ${token}` } : {};
                const payload: any = {
                  firstName: createForm.firstName.trim(),
                  lastName: createForm.lastName.trim(),
                  email: createForm.email.trim().toLowerCase(),
                  password: createForm.password,
                  role: createForm.role || 'field_worker',
                };
                if (createForm.phone) payload.phone = createForm.phone.trim();
                if (isAdmin && createForm.companyId) payload.company = createForm.companyId;

                const res = await axios.post(`${baseURL}/users`, payload, { headers });
          const created = res?.data?.data || res?.data;
          const newRow: UserRow = {
            id: String(created?._id || created?.id),
            name: String(`${created?.firstName ?? ''} ${created?.lastName ?? ''}`.trim() || created?.name || 'Unknown'),
            email: String(created?.email || '—'),
            role: String(created?.role || 'field_worker'),
            status: String(created?.isActive ? 'active' : 'inactive'),
            company: String((created?.company && (created.company.name || created.company.legalName)) || ''),
          };
                setUsers(prev => [newRow, ...prev]);
                setCreateOpen(false);
                setCreateForm({ firstName: '', lastName: '', email: '', password: '', role: 'field_worker', companyId: '', phone: '' });
              } catch (err) {
                console.error('Failed to create user:', err);
              } finally {
                setCreateLoading(false);
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;